import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, query, where, doc, deleteDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { getCurrentUser } from "../../auth";

export default function Announcements() {
  const user = getCurrentUser();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";
  const [campus, setCampus] = useState(String(user?.campus || "gapan").toLowerCase());
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [sectionsText, setSectionsText] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState("");
  const [pinned, setPinned] = useState(false);
  const [priority, setPriority] = useState("normal");
  const [imageUrl, setImageUrl] = useState("");

  async function load() {
    setBusy(true);
    try {
      let q = collection(db, "announcements");
      if (!isAdmin) q = query(q, where("campus", "==", campus));
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a,b)=>{
          const pa = a.pinned ? 0 : 1, pb = b.pinned ? 0 : 1;
          if (pa !== pb) return pa - pb;
          const ha = a.priority === "high" ? 0 : 1, hb = b.priority === "high" ? 0 : 1;
          if (ha !== hb) return ha - hb;
          return String(b.publishedAt||b.startsAt||"").localeCompare(String(a.publishedAt||a.startsAt||""));
        });
      setRows(list);
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => { load(); }, [campus]);

  async function post() {
    if (!title || !message || !campus) { setInfo("Campus, Title and Message are required."); return; }
    setBusy(true); setInfo("Posting…");
    const secs = sectionsText.split(",").map(s => s.trim()).filter(Boolean);
    try {
      await addDoc(collection(db, "announcements"), {
        campus,
        title,
        message,
        gradeLevel: gradeLevel || "",
        sections: secs,
        startsAt: startsAt || "",
        endsAt: endsAt || "",
        publishedAt: new Date().toISOString(),
        pinned: !!pinned,
        priority: priority || "normal",
        imageUrl: imageUrl || ""
      });
      setTitle(""); setMessage(""); setGradeLevel(""); setSectionsText(""); setStartsAt(""); setEndsAt(""); setPinned(false); setPriority("normal"); setImageUrl("");
      setInfo("Posted.");
      await load();
    } catch (e) {
      setInfo("Failed to post announcement.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    setBusy(true);
    try { await deleteDoc(doc(db, "announcements", id)); await load(); }
    finally { setBusy(false); }
  }

  return (
    <div className="container mt-4">
      <h3 className="mb-3">Announcements</h3>
      <div className="card p-3 mb-3">
        <div className="row g-2">
          {isAdmin && (
            <div className="col-md-3">
              <label className="form-label">Campus</label>
              <select className="form-select" value={campus} onChange={e => setCampus(e.target.value)}>
                <option value="gapan">Gapan</option>
                <option value="bulacan">Bulacan</option>
              </select>
            </div>
          )}
          <div className="col-md-3">
            <label className="form-label">Grade Level (optional)</label>
            <input className="form-control" value={gradeLevel} onChange={e => setGradeLevel(e.target.value)} placeholder="e.g., Grade 1" />
          </div>
          <div className="col-md-3">
            <label className="form-label">Sections (optional)</label>
            <input className="form-control" value={sectionsText} onChange={e => setSectionsText(e.target.value)} placeholder="comma-separated" />
          </div>
          <div className="col-md-3">
            <label className="form-label">Starts At (optional)</label>
            <input type="date" className="form-control" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Ends At (optional)</label>
            <input type="date" className="form-control" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
          </div>
          <div className="col-md-6">
            <label className="form-label">Image URL (optional)</label>
            <input className="form-control" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://example.com/banner.jpg" />
          </div>
          <div className="col-12">
            <label className="form-label">Title</label>
            <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} />
          </div>
          <div className="col-12">
            <label className="form-label">Message</label>
            <textarea rows="4" className="form-control" value={message} onChange={e => setMessage(e.target.value)} />
          </div>
          <div className="col-md-3">
            <label className="form-label">Priority</label>
            <select className="form-select" value={priority} onChange={e => setPriority(e.target.value)}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          <div className="col-md-3 d-flex align-items-center">
            <div className="form-check mt-4">
              <input className="form-check-input" type="checkbox" id="pinned" checked={pinned} onChange={e => setPinned(e.target.checked)} />
              <label htmlFor="pinned" className="form-check-label">Pin banner</label>
            </div>
          </div>
          <div className="col-12 d-flex gap-2">
            <button className="btn btn-primary" onClick={post} disabled={busy}>Post Announcement</button>
            <button className="btn btn-outline-secondary" onClick={load} disabled={busy}>Reload</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light"><tr><th>Campus</th><th>Title</th><th>Grade</th><th>Sections</th><th>Priority</th><th>Pinned</th><th>Active</th><th></th></tr></thead>
            <tbody>
              {rows.map(r => {
                const active = (() => {
                  const today = new Date().toISOString().slice(0,10);
                  if (r.startsAt && today < r.startsAt) return "Scheduled";
                  if (r.endsAt && today > r.endsAt) return "Expired";
                  return "Active";
                })();
                return (
                  <tr key={r.id}>
                    <td className="text-capitalize">{r.campus}</td>
                    <td>{r.title}</td>
                    <td>{r.gradeLevel || ""}</td>
                    <td>{Array.isArray(r.sections) ? r.sections.join(", ") : ""}</td>
                    <td className={r.priority==="high"?"text-danger fw-bold":""}>{r.priority || "normal"}</td>
                    <td>{r.pinned ? "Yes" : ""}</td>
                    <td>{active}</td>
                    <td className="text-end">
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(r.id)} disabled={busy}>Delete</button>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan="6" className="text-center text-muted">No announcements</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {info && <div className="alert alert-info mt-3">{info}</div>}
    </div>
  );
}
